import {
  CheckboxWithLabel, DropdownWithLabel,
  memoWithOpen,
  FileWithLabel,
  SimpleDialog
} from "../../components";
import React, {useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {useTaskQueue, useViewer} from "../../hooks";
import {
  Checkbox,
  createStyles,
  Grid,
  InputLabel, ListItemText,
  makeStyles,
  MenuItem,
  Select,
  Theme,
  Typography
} from "@material-ui/core";
import {lasFileFilter} from "../../file-extensions";
import {deleteAsset} from "../../redux/assets-slice";
import clsx from "clsx";
import {classifications} from "../../classifications";
import { useTranslation } from "react-i18next";
import { useAssetTools } from "../../utilities";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    flexibleHeight: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      "& span": {
        flex: 1
      }
    },
    multipleSelectWithLabel: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      "& span": {
        flex: 1
      }
    },
    paddingBottom: {
      paddingBottom: `${theme.spacing(1)}px !important`,
    },
    inputParent: {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      minHeight: theme.spacing(3),
      paddingBottom: theme.spacing(0.5)
    }
  })
);

enum OperationTypes {
  Include = "include",
  Exclude = "exclude"
}

export const ClassifyExport = memoWithOpen((props) => {
  const {assets, open, onClose} = props;
  const {viewer} = useViewer();
  const dispatch = useDispatch();
  const classes = useStyles();
  const {addRunningTask} = useTaskQueue();
  const {addAssetPaths} = useAssetTools();
  const {t} = useTranslation();

  const [outputPath, setOutputPath] = useState(null);
  const [operationType, setOperationType] = useState(OperationTypes.Include);
  const [classValues, setClassValues] = useState([]);
  const [importResult, setImportResult] = useState(true);

  // TODO: actually handle multiple inputs (sent multiple exe commands to processing queue)
  const asset = assets ? assets[0] : undefined;

  const classList = useMemo(() => {
    if (!open || !asset) return new Set<number>();
    const pointCloud = viewer.pointclouds.getByID(asset.id);
    return pointCloud.uniqueClassifications;
  }, [open, asset]);

  const canSubmit = (outputPath !== null)
    && (operationType !== null)
    && (classValues.length > 0);

  const handleSubmit = () => {
    const pythonName = "classify_export";

    let commands = [
      "-p", pythonName,
      "--inputlas", asset.path,
      "--outputlas", outputPath,
      "--operation", operationType,
      "--classes", classValues.toString()
    ];

    const afterClose = async () => {
      if (!importResult) return;

      // Remove old LAS
      dispatch(deleteAsset(asset.id));

      // Add new thinned LAS
      await addAssetPaths([outputPath], asset.folderID);
    };
    onClose();
    addRunningTask(commands, afterClose);
  };

  const handleClassChange = (event) => {
    setClassValues(event.target.value);
  };

  // Function of how the classes vals are rendered and shown in dropdown
  const renderClasses = (selected) => {
    const renderedClasses = [];
    selected.forEach((classVal) => {
      renderedClasses.push(formatClassValue(classVal));
    });
    return renderedClasses.join(" ,");
  };

  const formatClassValue = (classValue) => {
    return classifications[classValue].name + ` (${classValue})`;
  };

  const resetDefaults = () => {
    setOutputPath(null);
    setOperationType(OperationTypes.Include);
    setClassValues([]);
    setImportResult(true);
  };

  useEffect(() => {
    if (open) return;
    resetDefaults();
  }, [open]);

  return (
    <React.Fragment>
      <SimpleDialog
        open={open}
        onClose={onClose}
        onSubmit={handleSubmit}
        canSubmit={canSubmit}
        title={t('functions.classify_export.title')}
        button={t('buttons.submit')}
      >
        <Grid container spacing={2}>

          {/** Operation Type */}
          <Grid item xs={6}>
            <DropdownWithLabel
              title={t('functions.classify_export.operation_type')}
              label={t('functions.classify_export.operation_description')}
              onChange={setOperationType}
              value={operationType}
              options={[
                [OperationTypes.Include, t('functions.classify_export.include')],
                [OperationTypes.Exclude, t('functions.classify_export.exclude')]
              ]}
            />
          </Grid>

          {/** Classes */}
          <Grid item xs={6}>
            <div className={clsx(classes.multipleSelectWithLabel, classes.paddingBottom, classes.flexibleHeight)}>
              <div className={classes.inputParent}>
                <InputLabel>
                  {t('functions.classify_export.classes')}
                </InputLabel>
              </div>

              <Typography variant="caption">
                {t('functions.classify_export.classes_description')}
              </Typography>

              <Select
                multiple
                value={classValues}
                onChange={handleClassChange}
                renderValue={renderClasses}
              >
                {Array.from(classList.values()).map((classValue) => {
                  return (
                    <MenuItem key={classValue} value={classValue}>
                      <Checkbox checked={classValues.indexOf(classValue) > -1} />
                      <ListItemText primary={formatClassValue(classValue)} />
                    </MenuItem>
                  );
                })}
              </Select>
            </div>
          </Grid>

          {/** Import results */}
          <Grid item xs={6}>
            <CheckboxWithLabel
              title={t('functions.import_results')}
              label={t('functions.classify_export.import_results_description')}
              value={importResult}
              onChange={setImportResult}
            />
          </Grid>

          {/** Output LAS */}
          <Grid item xs={6}>
            <FileWithLabel
              title={t('functions.classify_export.output_las')}
              label={t('functions.classify_export.output_las_description')}
              filter={lasFileFilter}
              value={outputPath}
              onChange={setOutputPath}
              saveFile
            />
          </Grid>

        </Grid>
      </SimpleDialog>
    </React.Fragment>
  );
});
